import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Toolbar,
  Tooltip,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Print,
  FileDownload,
  PictureAsPdf,
  TableChart,
  DateRange,
  Search,
  Refresh,
  Assessment,
  TrendingUp,
  Inventory,
  People,
  ShoppingCart,
  Close,
  FilterList,
  CalendarToday,
  CalendarMonth,
  Today,
  TrendingDown,
  AccountBalance,
  Payment,
  CreditCard,
  Money,
  CardGiftcard,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../services/api';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filters, setFilters] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
    product: '',
    category: '',
    customer: '',
    transId: '',
    status: ''
  });

  const reportCategories = [
    { id: 'all', label: 'All Reports', icon: Assessment, color: '#1976d2' },
    { id: 'sales', label: 'Sales Reports', icon: TrendingUp, color: '#2e7d32' },
    { id: 'inventory', label: 'Inventory Reports', icon: Inventory, color: '#ed6c02' },
    { id: 'customer', label: 'Customer Reports', icon: People, color: '#9c27b0' },
    { id: 'transaction', label: 'Transaction Reports', icon: ShoppingCart, color: '#d32f2f' },
    { id: 'bir', label: 'BIR Reports', icon: AccountBalance, color: '#1565c0' },
  ];

  const reportTypes = [
    { 
      id: 'sales_overtime', 
      label: 'Sales Over Time', 
      description: 'Track sales performance and trends over time periods',
      category: 'sales',
      endpoint: 'sales_overtime',
      icon: TrendingUp,
      columns: [
        { key: 'trans_date', label: 'Date', format: 'date' },
        { key: 'subtotal_amount', label: 'Subtotal', format: 'currency' },
        { key: 'discount_amount', label: 'Discounts', format: 'currency' },
        { key: 'service_charge_amount', label: 'Service Charges', format: 'currency' },
        { key: 'total_amount', label: 'Total Sales', format: 'currency' },
        { key: 'trans_count', label: 'Transactions' },
        { key: 'avg_trans_value', label: 'Avg Transaction', format: 'currency' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'sales_by_product', 
      label: 'Sales by Product', 
      description: 'Analyze sales performance for individual products',
      category: 'sales',
      endpoint: 'sales_by_product',
      icon: ShoppingCart,
      columns: [
        { key: 'sku', label: 'SKU' },
        { key: 'name', label: 'Product Name' },
        { key: 'category', label: 'Category' },
        { key: 'total_sold', label: 'Qty Sold' },
        { key: 'amount', label: 'Total Sales', format: 'currency' },
        { key: 'avg_cost', label: 'Avg Cost', format: 'currency' }
      ],
      filters: ['from', 'to', 'product']
    },
    { 
      id: 'sales_by_category', 
      label: 'Sales by Category', 
      description: 'Compare sales performance across product categories',
      category: 'sales',
      endpoint: 'sales_by_category',
      icon: Assessment,
      columns: [
        { key: 'category', label: 'Category' },
        { key: 'units_sold', label: 'Units Sold' },
        { key: 'amount', label: 'Total Sales', format: 'currency' },
        { key: 'avg_cost', label: 'Avg Cost', format: 'currency' },
        { key: 'percentage', label: 'Percentage' }
      ],
      filters: ['from', 'to', 'category']
    },
    { 
      id: 'daily_sales', 
      label: 'Daily Sales Report', 
      description: 'Detailed daily sales breakdown and performance',
      category: 'sales',
      endpoint: 'daily_sales',
      icon: Today,
      columns: [
        { key: 'sale_date', label: 'Date', format: 'date' },
        { key: 'transaction_count', label: 'Transactions' },
        { key: 'gross_sales', label: 'Gross Sales', format: 'currency' },
        { key: 'discounts', label: 'Discounts', format: 'currency' },
        { key: 'service_charges', label: 'Service Charges', format: 'currency' },
        { key: 'net_sales', label: 'Net Sales', format: 'currency' },
        { key: 'avg_transaction', label: 'Avg Transaction', format: 'currency' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'weekly_sales', 
      label: 'Weekly Sales Report', 
      description: 'Weekly sales summary and trends analysis',
      category: 'sales',
      endpoint: 'weekly_sales',
      icon: CalendarToday,
      columns: [
        { key: 'week_start', label: 'Week Starting', format: 'date' },
        { key: 'week_end', label: 'Week Ending', format: 'date' },
        { key: 'transaction_count', label: 'Transactions' },
        { key: 'gross_sales', label: 'Gross Sales', format: 'currency' },
        { key: 'discounts', label: 'Discounts', format: 'currency' },
        { key: 'service_charges', label: 'Service Charges', format: 'currency' },
        { key: 'net_sales', label: 'Net Sales', format: 'currency' },
        { key: 'growth_rate', label: 'Growth %', format: 'percentage' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'monthly_sales', 
      label: 'Monthly Sales Report', 
      description: 'Monthly sales performance and comparison',
      category: 'sales',
      endpoint: 'monthly_sales',
      icon: CalendarMonth,
      columns: [
        { key: 'month_year', label: 'Month' },
        { key: 'transaction_count', label: 'Transactions' },
        { key: 'gross_sales', label: 'Gross Sales', format: 'currency' },
        { key: 'discounts', label: 'Discounts', format: 'currency' },
        { key: 'service_charges', label: 'Service Charges', format: 'currency' },
        { key: 'net_sales', label: 'Net Sales', format: 'currency' },
        { key: 'avg_daily_sales', label: 'Avg Daily Sales', format: 'currency' },
        { key: 'growth_rate', label: 'Growth %', format: 'percentage' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'gross_discount_net_sales', 
      label: 'Gross, Discount & Net Sales', 
      description: 'Breakdown of gross sales, discounts applied, and net sales',
      category: 'sales',
      endpoint: 'gross_discount_net_sales',
      icon: TrendingDown,
      columns: [
        { key: 'sale_date', label: 'Date', format: 'date' },
        { key: 'gross_sales', label: 'Gross Sales', format: 'currency' },
        { key: 'total_discounts', label: 'Total Discounts', format: 'currency' },
        { key: 'service_charges', label: 'Service Charges', format: 'currency' },
        { key: 'discount_percentage', label: 'Discount %', format: 'percentage' },
        { key: 'net_sales', label: 'Net Sales', format: 'currency' },
        { key: 'discount_ratio', label: 'Discount Ratio', format: 'percentage' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'sales_by_payment_type', 
      label: 'Sales by Payment Type', 
      description: 'Sales breakdown by payment method (Cash, GCash, Card, etc.)',
      category: 'sales',
      endpoint: 'sales_by_payment_type',
      icon: Payment,
      columns: [
        { key: 'payment_type', label: 'Payment Type' },
        { key: 'transaction_count', label: 'Transactions' },
        { key: 'total_amount', label: 'Total Amount', format: 'currency' },
        { key: 'percentage', label: 'Percentage', format: 'percentage' },
        { key: 'avg_transaction', label: 'Avg Transaction', format: 'currency' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'complimentary_items', 
      label: 'Complimentary Items Report', 
      description: 'Track all complimentary items given out with transaction details',
      category: 'sales',
      endpoint: 'complimentary_items',
      icon: CardGiftcard,
      columns: [
        { key: 'transaction_date', label: 'Date', format: 'datetime' },
        { key: 'txn_no', label: 'Transaction #' },
        { key: 'sku', label: 'SKU' },
        { key: 'product_name', label: 'Product Name' },
        { key: 'qty', label: 'Quantity' },
        { key: 'unit_price', label: 'Original Price', format: 'currency' },
        { key: 'total_value', label: 'Total Value', format: 'currency' },
        { key: 'customer', label: 'Customer' },
        { key: 'cashier', label: 'Cashier' }
      ],
      filters: ['from', 'to', 'product']
    },
    { 
      id: 'sales_by_customer', 
      label: 'Sales by Customer', 
      description: 'Analyze customer purchase patterns and loyalty',
      category: 'customer',
      endpoint: 'sales_by_customer',
      icon: People,
      columns: [
        { key: 'customer', label: 'Customer' },
        { key: 'contact', label: 'Contact' },
        { key: 'transaction_count', label: 'Transactions' },
        { key: 'amount', label: 'Total Sales', format: 'currency' }
      ],
      filters: ['from', 'to', 'customer']
    },
    { 
      id: 'transaction_summary', 
      label: 'Transaction Summary', 
      description: 'Detailed breakdown of all transactions',
      category: 'transaction',
      endpoint: 'transaction_summary',
      icon: TableChart,
      columns: [
        { key: 'txn_no', label: 'Transaction #' },
        { key: 'transaction_date', label: 'Date', format: 'datetime' },
        { key: 'customer', label: 'Customer' },
        { key: 'status', label: 'Status' },
        { key: 'amount', label: 'Amount', format: 'currency' },
        { key: 'payment_modes', label: 'Payment Methods' }
      ],
      filters: ['from', 'to', 'transId']
    },
    { 
      id: 'product_on_hand', 
      label: 'Product Inventory', 
      description: 'Current stock levels and inventory valuation',
      category: 'inventory',
      endpoint: 'product_on_hand',
      icon: Inventory,
      columns: [
        { key: 'sku', label: 'SKU' },
        { key: 'product', label: 'Product' },
        { key: 'product_code', label: 'Code' },
        { key: 'category', label: 'Category' },
        { key: 'on_hand', label: 'On Hand' },
        { key: 'unit', label: 'Unit' },
        { key: 'unit_cost', label: 'Unit Cost', format: 'currency' },
        { key: 'total_value', label: 'Total Value', format: 'currency' },
        { key: 'last_received', label: 'Last Received', format: 'date' },
        { key: 'last_sold', label: 'Last Sold', format: 'date' }
      ],
      filters: ['product']
    },
    { 
      id: 'inventory_closing', 
      label: 'Inventory Closing', 
      description: 'Track inventory adjustments and closing activities',
      category: 'inventory',
      endpoint: 'inventory_closing',
      icon: Assessment,
      columns: [
        { key: 'id', label: 'ID' },
        { key: 'closing_date', label: 'Closing Date', format: 'date' },
        { key: 'status', label: 'Status', format: 'status' },
        { key: 'created_by_name', label: 'Created By' },
        { key: 'item_count', label: 'Items' },
        { key: 'total_system_qty', label: 'System Qty' },
        { key: 'total_physical_qty', label: 'Physical Qty' },
        { key: 'total_variance_qty', label: 'Variance Qty', format: 'variance' },
        { key: 'positive_variance', label: 'Gains' },
        { key: 'negative_variance', label: 'Losses' },
        { key: 'total_value', label: 'Total Value', format: 'currency' },
        { key: 'positive_variance_value', label: 'Gain Value', format: 'currency' },
        { key: 'negative_variance_value', label: 'Loss Value', format: 'currency' }
      ],
      filters: ['from', 'to', 'status']
    },
    // BIR Reports
    { 
      id: 'z_read', 
      label: 'Z-Read Report (Daily Sales Report)', 
      description: 'Daily sales report with VAT breakdown, service charges, discounts, and OR numbers. Resets counters after generation.',
      category: 'bir',
      endpoint: 'z_read',
      icon: Today,
      columns: [
        { key: 'report_date', label: 'Date', format: 'date' },
        { key: 'beginning_or', label: 'Beginning OR#' },
        { key: 'ending_or', label: 'Ending OR#' },
        { key: 'gross_sales', label: 'Gross Sales', format: 'currency' },
        { key: 'vatable_sales', label: 'VATable Sales', format: 'currency' },
        { key: 'vat_exempt_sales', label: 'VAT-Exempt Sales', format: 'currency' },
        { key: 'zero_rated_sales', label: 'Zero-Rated Sales', format: 'currency' },
        { key: 'vat_amount', label: 'VAT Amount', format: 'currency' },
        { key: 'total_discounts', label: 'Total Discounts', format: 'currency' },
        { key: 'service_charges', label: 'Service Charges', format: 'currency' },
        { key: 'voided_amount', label: 'Voided Transactions', format: 'currency' },
        { key: 'net_sales', label: 'Net Sales', format: 'currency' },
        { key: 'transaction_count', label: 'Transaction Count' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'x_read', 
      label: 'X-Read Report (Intermediate Report)', 
      description: 'Intermediate sales report with same data as Z-Read but does not reset counters. Can be generated anytime.',
      category: 'bir',
      endpoint: 'x_read',
      icon: CalendarToday,
      columns: [
        { key: 'report_date', label: 'Date', format: 'date' },
        { key: 'beginning_or', label: 'Beginning OR#' },
        { key: 'ending_or', label: 'Ending OR#' },
        { key: 'gross_sales', label: 'Gross Sales', format: 'currency' },
        { key: 'vatable_sales', label: 'VATable Sales', format: 'currency' },
        { key: 'vat_exempt_sales', label: 'VAT-Exempt Sales', format: 'currency' },
        { key: 'zero_rated_sales', label: 'Zero-Rated Sales', format: 'currency' },
        { key: 'vat_amount', label: 'VAT Amount', format: 'currency' },
        { key: 'total_discounts', label: 'Total Discounts', format: 'currency' },
        { key: 'service_charges', label: 'Service Charges', format: 'currency' },
        { key: 'voided_amount', label: 'Voided Transactions', format: 'currency' },
        { key: 'net_sales', label: 'Net Sales', format: 'currency' },
        { key: 'transaction_count', label: 'Transaction Count' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'monthly_sales_summary', 
      label: 'Monthly Sales Summary', 
      description: 'Consolidated monthly totals from Z-Read reports with VAT breakdowns and growth analysis.',
      category: 'bir',
      endpoint: 'monthly_sales_summary',
      icon: CalendarMonth,
      columns: [
        { key: 'month_year', label: 'Month' },
        { key: 'total_transactions', label: 'Total Transactions' },
        { key: 'gross_sales', label: 'Gross Sales', format: 'currency' },
        { key: 'vatable_sales', label: 'VATable Sales', format: 'currency' },
        { key: 'vat_exempt_sales', label: 'VAT-Exempt Sales', format: 'currency' },
        { key: 'zero_rated_sales', label: 'Zero-Rated Sales', format: 'currency' },
        { key: 'total_vat_amount', label: 'Total VAT', format: 'currency' },
        { key: 'total_discounts', label: 'Total Discounts', format: 'currency' },
        { key: 'total_service_charges', label: 'Total Service Charges', format: 'currency' },
        { key: 'total_voided', label: 'Total Voided', format: 'currency' },
        { key: 'net_sales', label: 'Net Sales', format: 'currency' },
        { key: 'growth_rate', label: 'Growth Rate', format: 'percentage' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'annual_sales_summary', 
      label: 'Annual Sales Summary', 
      description: 'Consolidated annual totals per month for the year with complete VAT analysis.',
      category: 'bir',
      endpoint: 'annual_sales_summary',
      icon: Assessment,
      columns: [
        { key: 'year', label: 'Year' },
        { key: 'month', label: 'Month' },
        { key: 'total_transactions', label: 'Transactions' },
        { key: 'gross_sales', label: 'Gross Sales', format: 'currency' },
        { key: 'vatable_sales', label: 'VATable Sales', format: 'currency' },
        { key: 'vat_exempt_sales', label: 'VAT-Exempt', format: 'currency' },
        { key: 'zero_rated_sales', label: 'Zero-Rated', format: 'currency' },
        { key: 'vat_amount', label: 'VAT Amount', format: 'currency' },
        { key: 'total_discounts', label: 'Discounts', format: 'currency' },
        { key: 'service_charges', label: 'Service Charges', format: 'currency' },
        { key: 'voided_amount', label: 'Voided', format: 'currency' },
        { key: 'net_sales', label: 'Net Sales', format: 'currency' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'vat_breakdown', 
      label: 'VAT Breakdown Report', 
      description: 'Detailed VAT analysis separating VATable, VAT-exempt, and zero-rated sales with computed VAT amounts.',
      category: 'bir',
      endpoint: 'vat_breakdown',
      icon: Money,
      columns: [
        { key: 'transaction_date', label: 'Date', format: 'date' },
        { key: 'txn_no', label: 'OR Number' },
        { key: 'gross_amount', label: 'Gross Amount', format: 'currency' },
        { key: 'vatable_sales', label: 'VATable Sales', format: 'currency' },
        { key: 'vat_exempt_sales', label: 'VAT-Exempt Sales', format: 'currency' },
        { key: 'zero_rated_sales', label: 'Zero-Rated Sales', format: 'currency' },
        { key: 'vat_amount', label: 'VAT Amount (12%)', format: 'currency' },
        { key: 'net_amount', label: 'Net Amount', format: 'currency' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'payment_method', label: 'Payment Method' }
      ],
      filters: ['from', 'to']
    },
    { 
      id: 'discount_void_report', 
      label: 'Discount and Void Report', 
      description: 'Comprehensive breakdown of all discounts (senior citizen, PWD, promotional) and voided transactions with timestamps and reasons.',
      category: 'bir',
      endpoint: 'discount_void_report',
      icon: TrendingDown,
      columns: [
        { key: 'transaction_date', label: 'Date', format: 'datetime' },
        { key: 'txn_no', label: 'OR Number' },
        { key: 'type', label: 'Type' },
        { key: 'discount_type', label: 'Discount Type' },
        { key: 'original_amount', label: 'Original Amount', format: 'currency' },
        { key: 'discount_amount', label: 'Discount Amount', format: 'currency' },
        { key: 'final_amount', label: 'Final Amount', format: 'currency' },
        { key: 'reason', label: 'Reason/Notes' },
        { key: 'authorized_by', label: 'Authorized By' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'senior_pwd_id', label: 'Senior/PWD ID' }
      ],
      filters: ['from', 'to']
    }
  ];

  // Filter reports based on search and category
  const filteredReports = reportTypes.filter(report => {
    const matchesSearch = report.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         report.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || report.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleReportSelect = (report) => {
    setSelectedReport(report);
    setReportData([]);
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setSelectedReport(null);
    setReportData([]);
  };

  const fetchReportData = async () => {
    if (!selectedReport) return;
    
    setLoading(true);
    try {
      let url = `api/v1/reports/${selectedReport.endpoint}`;
      
      // Build URL with parameters based on report requirements
      if (selectedReport.filters.includes('from') && selectedReport.filters.includes('to')) {
        url += `/${format(filters.from, 'yyyy-MM-dd')}/${format(filters.to, 'yyyy-MM-dd')}`;
      }
      
      if (selectedReport.filters.includes('product') && filters.product) {
        url += `/${encodeURIComponent(filters.product)}`;
      } else if (selectedReport.filters.includes('product')) {
        url += '/';
      }
      
      if (selectedReport.filters.includes('category') && filters.category) {
        url += `/${encodeURIComponent(filters.category)}`;
      } else if (selectedReport.filters.includes('category')) {
        url += '/';
      }
      
      if (selectedReport.filters.includes('customer') && filters.customer) {
        url += `/${encodeURIComponent(filters.customer)}`;
      } else if (selectedReport.filters.includes('customer')) {
        url += '/';
      }
      
      if (selectedReport.filters.includes('transId') && filters.transId) {
        url += `/${encodeURIComponent(filters.transId)}`;
      } else if (selectedReport.filters.includes('transId')) {
        url += '/';
      }
      
      if (selectedReport.filters.includes('status') && filters.status) {
        url += `/${encodeURIComponent(filters.status)}`;
      } else if (selectedReport.filters.includes('status')) {
        url += '/';
      }

      const response = await api.get(url);
      console.log('Report Name:', selectedReport.label);
      console.log('API Endpoint:', url);
      console.log('Report data response:', response.data.data);
      
      setReportData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching report data:', error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const formatCellValue = (value, formatType) => {
    if (!value && value !== 0) return '-';
    
    switch (formatType) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      case 'date':
        return value ? format(new Date(value), 'yyyy-dd-MM') : '-';
      case 'datetime':
        return value ? format(new Date(value), 'yyyy-dd-MM hh:mma') : '-';
      case 'status':
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            backgroundColor: value === 'finalized' ? '#e8f5e8' : '#fff3cd',
            color: value === 'finalized' ? '#2e7d32' : '#856404',
            textTransform: 'capitalize'
          }}>
            {value}
          </span>
        );
      case 'variance':
        if (value === 0) return '0';
        return (
          <span style={{
            color: value > 0 ? '#2e7d32' : '#d32f2f',
            fontWeight: 'bold'
          }}>
            {value > 0 ? '+' : ''}{value}
          </span>
        );
      case 'percentage':
        return `${Number(value).toFixed(2)}%`;
      default:
        return value;
    }
  };

  const renderFilters = () => {
    if (!selectedReport) return null;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
            Report Filters
          </Typography>
          <Grid container spacing={2}>
            {selectedReport.filters.includes('from') && (
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="From Date"
                  value={filters.from}
                  onChange={(date) => handleFilterChange('from', date)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
            )}
            
            {selectedReport.filters.includes('to') && (
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="To Date"
                  value={filters.to}
                  onChange={(date) => handleFilterChange('to', date)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
            )}
            
            {selectedReport.filters.includes('product') && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Product"
                  value={filters.product}
                  onChange={(e) => handleFilterChange('product', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            )}
            
            {selectedReport.filters.includes('category') && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Category"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            )}
            
            {selectedReport.filters.includes('customer') && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Customer"
                  value={filters.customer}
                  onChange={(e) => handleFilterChange('customer', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            )}
            
            {selectedReport.filters.includes('transId') && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Transaction ID"
                  value={filters.transId}
                  onChange={(e) => handleFilterChange('transId', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            )}
            
            {selectedReport.filters.includes('status') && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="finalized">Finalized</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                onClick={fetchReportData}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Search />}
                fullWidth
              >
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    if (reportData.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(
      reportData.map(row => {
        const formattedRow = {};
        selectedReport.columns.forEach(col => {
          formattedRow[col.label] = formatCellValue(row[col.key], col.format);
        });
        return formattedRow;
      })
    );
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, selectedReport.label);
    XLSX.writeFile(wb, `${selectedReport.label}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const handleExportPDF = () => {
    if (reportData.length === 0) return;

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(selectedReport.label, 14, 22);
    doc.setFontSize(10);
    doc.text(`Generated on ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, 14, 32);

    autoTable(doc, {
      head: [selectedReport.columns.map(col => col.label)],
      body: reportData.map(row => 
        selectedReport.columns.map(col => formatCellValue(row[col.key], col.format))
      ),
      startY: 40,
    });

    doc.save(`${selectedReport.label}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        p: 3, 
        height: '100vh',
        overflow: 'auto'
      }}>
        <Typography variant="h4" gutterBottom>
          Reports Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Generate comprehensive reports to analyze your business performance
        </Typography>

        {/* Search and Category Filter */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Category"
                  >
                    {reportCategories.map(category => (
                      <MenuItem key={category.id} value={category.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <category.icon sx={{ mr: 1, color: category.color }} />
                          {category.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Category Chips */}
        <Box sx={{ mb: 3 }}>
          {reportCategories.map(category => (
            <Chip
              key={category.id}
              label={category.label}
              icon={<category.icon />}
              onClick={() => setSelectedCategory(category.id)}
              color={selectedCategory === category.id ? 'primary' : 'default'}
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>

        {/* Reports Grid */}
        <Box sx={{ 
          height: 'calc(100vh - 400px)', 
          overflow: 'auto',
          p: 2
        }}>
          <Grid container spacing={3}>
            {filteredReports.map((report) => (
              <Grid item key={report.id} xs={12} sm={6} md={4}>
                <Card 
                  sx={{ 
                    height: '220px', 
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => handleReportSelect(report)}
                >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <report.icon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" component="div">
                        {report.label}
                      </Typography>
                      <Chip 
                        label={reportCategories.find(cat => cat.id === report.category)?.label} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {report.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" startIcon={<Assessment />}>
                    Generate Report
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredReports.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No reports found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or category filter
            </Typography>
          </Box>
        )}
        </Box>

        {/* Report Dialog */}
        <Dialog 
          open={reportDialogOpen} 
          onClose={handleCloseReportDialog}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedReport && <selectedReport.icon sx={{ mr: 1 }} />}
                {selectedReport?.label}
                {reportData.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({reportData.length} records)
                  </Typography>
                )}
              </Box>
              <Box>
                {reportData.length > 0 && (
                  <>
                    <Tooltip title="Print Report">
                      <IconButton onClick={handlePrint}>
                        <Print />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export to Excel">
                      <IconButton onClick={handleExportExcel}>
                        <FileDownload />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export to PDF">
                      <IconButton onClick={handleExportPDF}>
                        <PictureAsPdf />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <IconButton onClick={handleCloseReportDialog}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {renderFilters()}
            
            {reportData.length > 0 && (
              <>
                <TableContainer component={Paper} variant="outlined" id="report-table">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {selectedReport?.columns.map((column) => (
                          <TableCell key={column.key} sx={{ fontWeight: 'bold' }}>
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.map((row, index) => (
                        <TableRow key={index} hover>
                          {selectedReport?.columns.map((column) => (
                            <TableCell key={column.key}>
                              {formatCellValue(row[column.key], column.format)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {reportData.length === 0 && !loading && selectedReport && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <TableChart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No data available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Adjust your filters and click "Generate Report" to load data
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
